import { InteractionRequiredAuthError } from "@azure/msal-browser"
import { apiConfig } from "../authConfig"
import { msalInstance } from "../index"
import { store } from "./store"

// Environment-aware API URL configuration
const API_BASE_URL =
  process.env.NODE_ENV !== "production"
    ? "http://localhost:8080"
    : window.location.origin

/**
 * Erstellt authentifizierte Headers für API-Anfragen
 * @returns Promise mit den Headers inklusive Authorization
 */
const getAuthenticatedHeaders = async (): Promise<HeadersInit> => {
  const state = store.getState()
  const account = state.authenticatedUser

  const headers: HeadersInit = {
    "Content-Type": "application/json",
  }

  // Add dev secret header if in development mode, regardless of auth status
  if (process.env.NODE_ENV !== "production" && process.env.REACT_APP_DEV_SECRET) {
    headers["X-Dev-Secret"] = process.env.REACT_APP_DEV_SECRET
    console.log("[PDF Service] Adding dev secret header")
  } else {
    console.log("[PDF Service] Dev secret not added:", {
      NODE_ENV: process.env.NODE_ENV,
      hasDevSecret: !!process.env.REACT_APP_DEV_SECRET
    })
  }

  if (account) {
    try {
      const response = await msalInstance.acquireTokenSilent({
        scopes: apiConfig.scopes,
        account: account,
      })

      return {
        ...headers,
        Authorization: `Bearer ${response.accessToken}`,
      }
    } catch (error) {
      console.error("[PDF Service] Silent token acquisition error:", error)
      if (error instanceof InteractionRequiredAuthError) {
        console.warn(
          "[PDF Service] Silent token acquisition failed. Attempting interactive redirect."
        )
        try {
          await msalInstance.acquireTokenRedirect({
            scopes: apiConfig.scopes,
            account: account,
          })
        } catch (redirectError) {
          console.error(
            "[PDF Service] Interactive token acquisition error:",
            redirectError
          )
        }
      }
    }
  }

  return headers
}

/**
 * Extrahiert den Dateinamen aus dem Content-Disposition Header
 * @param contentDisposition Der Content-Disposition Header
 * @returns Der extrahierte Dateiname oder einen Standardnamen
 */
const extractFileName = (contentDisposition: string | null): string => {
  if (!contentDisposition) return "document.pdf"

  const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
    contentDisposition
  )
  if (matches && matches[1]) {
    return matches[1].replace(/['"]/g, "")
  }

  return "document.pdf"
}

/**
 * Lädt eine PDF-Datei vom Server herunter
 * @param fileId Die ID der PDF-Datei
 * @returns Promise mit dem Blob der PDF-Datei und dem Dateinamen
 */
export const downloadPDF = async (
  fileId: string
): Promise<{ blob: Blob; fileName: string }> => {
  const headers = await getAuthenticatedHeaders()
  const response = await fetch(`${API_BASE_URL}/api/pdf/${fileId}`, {
    method: "GET",
    headers,
    credentials: "include",
  })
  
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Nicht autorisiert. Bitte melden Sie sich an.")
    }
    
    // Try to parse error message from response
    try {
      const errorData = await response.json()
      if (errorData.error) {
        throw new Error(errorData.error)
      }
    } catch {
      // If not JSON or no error field, use generic message
    }
    
    if (response.status === 404) {
      throw new Error("Datei nicht gefunden oder nur als Referenz vorhanden")
    }
    
    throw new Error("PDF konnte nicht heruntergeladen werden")
  }

  const fileName = extractFileName(response.headers.get("content-disposition"))
  const blob = await response.blob()

  return { blob, fileName }
}

/**
 * Erstellt eine URL für die PDF-Datei
 * @param fileId Die ID der PDF-Datei
 * @returns Promise mit der URL der PDF-Datei
 */
export const getPDFUrl = async (fileId: string): Promise<string> => {
  const { blob } = await downloadPDF(fileId)
  return window.URL.createObjectURL(blob)
}

/**
 * Initiert den Download einer PDF-Datei im Browser
 * @param fileId Die ID der PDF-Datei
 * @returns Promise, das sich auflöst, wenn der Download abgeschlossen ist
 */
export const handlePDFDownload = async (fileId: string): Promise<void> => {
  try {
    const { blob, fileName } = await downloadPDF(fileId)
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error("Fehler beim Herunterladen der PDF:", error)
    throw error
  }
}
